import { Box, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { Image } from '@trashlab/sitesapp/src/organizations/fulltilt/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/fulltilt/contexts/use-tenant-context';
import { Button } from '@trashlab/sitesapp/src/organizations/fulltilt/template/components/shared/button';
import { GoogleReviews } from '@trashlab/sitesapp/src/organizations/fulltilt/template/components/shared/google-reviews';
import Breadcrumbs from '@trashlab/sitesapp/src/organizations/fulltilt/template/components/shared/breadcrumbs';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/fulltilt/lib/utils/get-variant-value';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/fulltilt/lib/utils/use-theme-text-color';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const DumpsterHeroSection = ({
  title,
  content,
  heroImage,
  contentDataVariable,
  imageDataVariable,
}: {
  title: string;
  content: string;
  heroImage: string;
  contentDataVariable?: string;
  imageDataVariable?: string;
}) => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const { textColorClass, isLightBackground } = useThemeTextColor();

  const heroBackgroundImage = useVariantValue(
    'HOME_HERO_BACKGROUND_IMAGE',
    '/default/hero-section-background.svg',
  );

  const ctaButtonText = useVariantValue(
    'CTA_BUTTON_TEXT',
    'Explore our services',
  );

  const handleCardClick = () => {
    setTimeout(() => {
      const dumpsterTypesElement = document.getElementById('dumpster-types');
      if (dumpsterTypesElement) {
        const headerOffset = 80;
        const elementPosition =
          dumpsterTypesElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 300);
  };

  return (
    <Grid
      columns={{ initial: '1', xs: '2' }}
      width="auto"
      justify="center"
      align="center"
      className="relative min-h-[360px] overflow-hidden"
    >
      <Box className="absolute inset-0 -z-20">
        <Image
          src={heroBackgroundImage}
          alt="Background"
          className="w-full h-full object-cover"
        />
      </Box>
      <Box className="absolute inset-0 -z-10 bg-[var(--background-color)] opacity-90" />

      <Box className="absolute top-0 left-0 z-20 pt-4 pl-8 w-full">
        <Breadcrumbs title={title} light={isLightBackground} />
      </Box>

      <Box className="order-2 xs:order-1 relative z-10 flex-1 h-full min-h-[324px] xs:min-h-[450px] sm:min-h-[720px] overflow-hidden">
        <Box
          className="absolute inset-0 overflow-hidden"
          data-variable={imageDataVariable || 'HOME_HERO_IMAGE'}
        >
          <Image
            src={heroImage}
            alt="Dumpster"
            className="w-full h-full object-cover object-center"
          />
        </Box>
      </Box>

      <Flex
        direction="column"
        p="8"
        gap="3"
        className="order-1 xs:order-2 relative z-10 pt-16"
      >
        <Flex direction="column" gap="6">
          <Heading
            size={{ initial: '8', md: '9' }}
            className={textColorClass}
            data-variable="HOME_HERO_TITLE"
          >
            {title ??
              `${website?.organization?.name || "Our company's"} rental ${
                orgAddress?.city ?? 'San Francisco'
              }`}
          </Heading>

          <Text
            size={{ initial: '2', md: '4' }}
            className={textColorClass}
            data-variable={contentDataVariable || 'HERO_SUBTITLE'}
          >
            {content}
          </Text>

          <Box className="flex col-span-1 justify-start">
            <Button
              withArrow
              onClick={handleCardClick}
              data-variable="CTA_BUTTON_TEXT"
            >
              {ctaButtonText}
            </Button>
          </Box>

          <GoogleReviews dark={!isLightBackground} />
        </Flex>
      </Flex>
    </Grid>
  );
};

export default DumpsterHeroSection;
